/**
 * LookingAhead - plan_28, plan_29 (every theme), on Wealth -> Plan.
 *
 * plan_29: on Plan it's one small row ("Looking ahead ›"). Tapping it opens
 * its own page, with every fund already open and its goal shown.
 *
 * Pick a year and see what each fund will have at the end of it: today's
 * balance, plus planned money coming in, minus planned money going out, for
 * everything dated up to Dec 31 of that year that hasn't been ticked off.
 * Funds with a goal say "Full ✓" or how much they're short. Tap a fund to see
 * the sum line by line. Nothing here changes your current balances.
 */
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSwipeBack } from "../../utils/swipeBack";
import { formatCurrency, formatCurrencyWhole } from "../../utils/formatters";

const money = (v) => (Math.abs(v - Math.round(v)) < 0.005 ? formatCurrencyWhole(Math.round(v)) : formatCurrency(v));
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

const ACCENT = { calm: '#1F1E24', pink: '#C23A72', purple: '#8B5CE0', warm: '#2D5A3D', blue: '#4361EE' };

// How much a payment puts into (or takes out of) one fund.
const shareFor = (p, fundId) => {
  if (p.fund_allocations && p.fund_allocations[fundId]) return Number(p.fund_allocations[fundId]) || 0;
  const list = p.designated_funds || (p.designated_fund ? [p.designated_fund] : []);
  if (!(p.fund_allocations && Object.keys(p.fund_allocations).length) && list.includes(fundId)) return (Number(p.amount) || 0) / list.length;
  return 0;
};

/**
 * funds: [{ id, name, emoji, goal, balance }] (balance = what it has today)
 * payments: your future income list (coming in and going out)
 * tint(i): { wash } for the i-th fund
 */
export default function LookingAhead({ funds = [], payments = [], tint, look = 'pink' }) {
  const calm = look === 'calm';
  const y0 = new Date().getFullYear();
  const [year, setYear] = useState(y0 + 1);
  const [open, setOpen] = useState(false);
  const pageRef = useRef(null);
  useSwipeBack(() => pageRef.current, () => setOpen(false), open);
  const endKey = year + '-12-31';
  const pending = (payments || []).filter((p) => !p.moved_to_account && (!p.expected_date || String(p.expected_date).slice(0, 10) <= endKey));

  const rows = funds.map((f, i) => {
    const lines = [];
    pending.forEach((p) => {
      const v = shareFor(p, f.id);
      if (!v) return;
      const d = p.expected_date ? new Date(String(p.expected_date).slice(0, 10) + 'T12:00:00') : null;
      const out = p.kind === 'out';
      lines.push({ k: String(p.id), label: (out ? '− ' : '+ ') + (p.description || (out ? 'Spending' : 'Income')) + (d ? ' · ' + MON[d.getMonth()] + ' ' + d.getFullYear() : ''), v: out ? -v : v, d: d ? d.getTime() : 0 });
    });
    lines.sort((a, b) => a.d - b.d);
    const end = (Number(f.balance) || 0) + lines.reduce((s, l) => s + l.v, 0);
    const goal = Number(f.goal) || 0;
    return { f, i, lines, end, goal, full: goal > 0 && end >= goal - 0.005, short: goal > 0 ? Math.max(0, goal - end) : 0 };
  });
  const withGoal = rows.filter((r) => r.goal > 0);
  const fullN = withGoal.filter((r) => r.full).length;
  const shortBy = withGoal.reduce((s, r) => s + r.short, 0);

  if (!funds.length) return null;

  const PAGE_BG = { calm: '#F4F1FB', pink: '#FBEFF5', purple: '#F6F3FA', warm: '#FAF7F2', blue: '#EEF2FF' };
  const card = calm
    ? { background: '#FFFFFF', borderRadius: 22 }
    : { background: '#FFFFFF', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };

  // On Plan: one small row that opens the page.
  const row = (
    <button type="button" onClick={() => setOpen(true)} className="w-full flex items-center text-left" data-patch="plan_29" style={{ ...card, border: 0, gap: 12, padding: '13px 16px', marginBottom: 12 }}>
      <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 14, background: '#ECE6FA', fontSize: 19 }}>{'\u{1F52D}'}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#26252E' }}>Looking ahead</span>
        <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 1, lineHeight: 1.35 }}>What each fund will have after everything you've planned</span>
      </span>
      <span style={{ fontSize: 20, color: '#B4AFC2', flexShrink: 0 }}>{'›'}</span>
    </button>
  );

  const page = open && createPortal(
    <div ref={pageRef} data-swipe-layer="" className="fixed inset-0 overflow-y-auto" style={{ zIndex: 60, background: PAGE_BG[look] || PAGE_BG.pink, padding: 'calc(env(safe-area-inset-top, 0px) + 14px) 16px calc(env(safe-area-inset-bottom, 0px) + 40px)', boxShadow: '-10px 0 30px rgba(35,38,43,0.08)', overscrollBehavior: 'contain' }} data-patch="plan_29">
      <div className="flex items-center" style={{ gap: 10, marginBottom: 4 }}>
        <button type="button" aria-label="Back" onClick={() => setOpen(false)} className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 999, border: 0, background: '#FFFFFF', fontSize: 20, color: '#26252E', padding: 0 }}>{'‹'}</button>
        <span style={calm ? { fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em', color: '#26252E' } : { fontFamily: "'DM Serif Display', serif", fontSize: 26, color: '#1A1A1F' }}>{calm ? 'looking ahead' : 'Looking Ahead'}</span>
      </div>
      <p style={{ margin: '4px 2px 14px', fontSize: 13.5, color: '#7A7A82', lineHeight: 1.45 }}>What each fund will have at the end of the year: today's balance, plus what's planned to come in, minus what's planned to go out.</p>
      <div className="flex" style={{ gap: 6 }}>
        {[y0, y0 + 1, y0 + 2].map((y) => (
          <button key={y} type="button" onClick={() => setYear(y)} style={{ flex: 1, border: 0, borderRadius: 999, padding: '9px 2px', fontSize: 13, fontWeight: 600, background: year === y ? ACCENT[look] || ACCENT.pink : '#FFFFFF', color: year === y ? '#FFFFFF' : '#6C6879' }}>End of {y}</button>
        ))}
      </div>
      {withGoal.length > 0 && (
        <div className="flex" style={{ gap: 8, marginTop: 10 }}>
          <div style={{ flex: 1, borderRadius: 18, padding: '10px 12px', background: '#DFF2E7' }}>
            <span style={{ display: 'block', fontSize: 12, color: '#2F8756' }}>Full</span>
            <b style={{ display: 'block', fontSize: 16, fontWeight: 600, color: '#2F8756' }}>{fullN} of {withGoal.length}</b>
          </div>
          <div style={{ flex: 1, borderRadius: 18, padding: '10px 12px', background: shortBy > 0 ? '#FAE4ED' : '#DFF2E7' }}>
            <span style={{ display: 'block', fontSize: 12, color: shortBy > 0 ? '#C04B7C' : '#2F8756' }}>{shortBy > 0 ? 'Short by' : 'Every goal'}</span>
            <b style={{ display: 'block', fontSize: 16, fontWeight: 600, color: shortBy > 0 ? '#C04B7C' : '#2F8756' }}>{shortBy > 0 ? money(shortBy) : 'Filled ✓'}</b>
          </div>
        </div>
      )}
      <div style={{ ...card, padding: '4px 16px', marginTop: 12 }}>
        {rows.map((r, k) => {
          const t = tint ? tint(r.i) : null;
          const pct = r.goal > 0 ? Math.max(0, Math.min(100, r.end / r.goal * 100)) : 0;
          return (
            <div key={r.f.id} style={{ padding: '14px 0', borderTop: k ? '1px solid #EEEAF6' : 0 }}>
              <div className="flex items-center" style={{ gap: 11 }}>
                <span className="flex items-center justify-center flex-shrink-0" style={{ width: 38, height: 38, borderRadius: 13, background: calm && t ? t.wash : '#F7F5FB', fontSize: 18 }}>{r.f.emoji || '\u{1F4B0}'}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="truncate" style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#26252E' }}>{r.f.name}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 1 }}>{r.goal > 0 ? 'Goal ' + money(r.goal) : 'No goal set'}</span>
                </span>
                <span style={{ textAlign: 'right', flexShrink: 0 }}>
                  <b style={{ display: 'block', fontSize: 15.5, fontWeight: 600, color: r.end < 0 ? '#C04B7C' : '#26252E' }}>{money(r.end)}</b>
                  {r.goal > 0 && (
                    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, borderRadius: 999, padding: '3px 8px', marginTop: 3, background: r.full ? '#DFF2E7' : '#FAE4ED', color: r.full ? '#2F8756' : '#C04B7C' }}>{r.full ? 'Full ✓' : money(r.short) + ' short'}</span>
                  )}
                </span>
              </div>
              {r.goal > 0 && (
                <div style={{ height: 6, borderRadius: 9, background: '#F0EDF6', margin: '10px 0 0 49px', overflow: 'hidden' }}>
                  <div style={{ width: pct + '%', height: '100%', borderRadius: 9, background: r.full ? '#9ED9B6' : '#F2AFC8' }} />
                </div>
              )}
              <div style={{ background: calm ? '#F4F1FB' : '#F8F6FA', borderRadius: 16, padding: '10px 12px', margin: '10px 0 0 49px' }}>
                <div className="flex justify-between" style={{ fontSize: 13, color: '#55565C', padding: '3px 0' }}><span>Now</span><span>{money(Number(r.f.balance) || 0)}</span></div>
                {r.lines.length === 0 && <div style={{ fontSize: 13, color: '#8B879A', padding: '3px 0' }}>Nothing planned for this fund yet.</div>}
                {r.lines.map((l) => (
                  <div key={l.k} className="flex justify-between" style={{ fontSize: 13, color: '#55565C', padding: '3px 0', gap: 10 }}><span style={{ minWidth: 0 }}>{l.label}</span><span style={{ flexShrink: 0, color: l.v < 0 ? '#C04B7C' : '#2F8756' }}>{(l.v < 0 ? '−' : '+') + money(Math.abs(l.v))}</span></div>
                ))}
                <div className="flex justify-between" style={{ fontSize: 13, fontWeight: 600, color: '#26252E', borderTop: '1px solid #E4DEF2', marginTop: 4, paddingTop: 7, gap: 10 }}>
                  <span>End of {year}</span><span>{money(r.end)}{r.goal > 0 ? ' of ' + money(r.goal) + ' goal' : ''}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );

  return <>{row}{page}</>;
}
